import { useState, useContext } from "react"
import { useHistory } from 'react-router-dom';
import { User } from "@phoenixlan/phoenix.js";
import { Table, TableCell, TableHead, IconContainer, SelectableTableRow, TableRow, TableBody } from "../../components/table";
import { PageLoading } from "../../components/pageLoading";
import { CardContainer, DashboardContent, DashboardHeader, DashboardSubtitle, DashboardTitle, InnerContainer, InnerContainerRow, InnerContainerTitle, InputContainer, InputElement, InputLabel, InputSelect, RowBorder, PanelButton } from "../../components/dashboard";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight, faCheck, faMinus, faDownload } from "@fortawesome/free-solid-svg-icons";
import { AuthenticationContext } from "../../components/authentication";
import { Notice } from "../../components/containers/notice";
import { TimestampToDateTime } from "../../components/timestampToDateTime";

import { useCurrentEvent } from "../../hooks/events/useCurrentEvent";
import { useEventTickets } from "../../hooks/tickets/useEventTickets";
import { useBrand } from "../../contexts/brand";
import { hasAnyBrandPermission } from "../../utils/roles";

const SORTING_METHODS = {
    TICKET_ID: 1,
    TICKET_TYPE: 2,
    TICKET_OWNER: 3,
    TICKED_CHECKED_IN: 4
}

const SORTING_TYPES = {}
SORTING_TYPES[SORTING_METHODS.TICKET_ID] = (a, b) => a.ticket_id - b.ticket_id;
SORTING_TYPES[SORTING_METHODS.TICKET_TYPE] = (a, b) => a.ticket_type.name.localeCompare(b.ticket_type.name);
SORTING_TYPES[SORTING_METHODS.TICKET_OWNER] = (a, b) => a.owner.firstname.localeCompare(b.owner.firstname);
SORTING_TYPES[SORTING_METHODS.TICKED_CHECKED_IN] = (a, b) => b.checked_in - a.checked_in;

export const TicketList = () => {
    const { brandUuid, path } = useBrand();

    let history = useHistory();

    // Import the following React contexts:
    const authContext = useContext(AuthenticationContext);

    // Function availibility control:
    let viewTickets = false;

    const [ activeSortingMethod, setActiveSortingMethod] = useState(1);
    const [ search, setSearch ] = useState("");

    // Check if user has "admin" or "ticket_admin" role and make the following functions available:
    if (hasAnyBrandPermission(authContext.roles, brandUuid, ["ticket_admin"])) {
        viewTickets = true;
    }

    const { data: currentEvent, isLoading: isLoadingCurrentEvent } = useCurrentEvent(brandUuid);
    const { data: tickets = [], isLoading: isLoadingTickets } = useEventTickets(viewTickets ? currentEvent?.uuid : undefined);

    const exportCsv = () => {
        const headers = ["ID", "Owner", "Ticket Type"];
        const rows = processedTicketList.map((ticket) => [
            ticket.ticket_id,
            User.getFullName(ticket.owner),
            ticket.ticket_type.name
        ]);
        const csvContent = [headers, ...rows].map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(",")).join("\n");
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = "tickets.csv";
        link.click();
        URL.revokeObjectURL(url);
    };

    const loading = viewTickets && (
        isLoadingCurrentEvent ||
        (currentEvent && isLoadingTickets)
    );

    if(loading) {
        return (
            <PageLoading />
        )
    }

    // Ticket sorting and filtering
    let processedTicketList = tickets
    .filter((ticket) =>
        ticket.owner.firstname.toLowerCase().includes(search) ||
        ticket.owner.lastname.toLowerCase().includes(search) ||
        ticket.owner.username.toLowerCase().includes(search) ||
        ticket.ticket_id == search
    )
    .sort(SORTING_TYPES[activeSortingMethod]);

    if(viewTickets) {
        return (
            <>
                <DashboardHeader border>
                    <DashboardTitle>
                        Billetter
                    </DashboardTitle>
                    <DashboardSubtitle>
                        { search
                            ? "Viser " + processedTicketList.length + " av " + tickets.length + " salg for dette arrangementet"
                            : tickets.length + " salg for dette arrangementet"
                        }
                    </DashboardSubtitle>
                </DashboardHeader>
                <DashboardContent>
                    <InnerContainer visible={!currentEvent}>
                        <InnerContainerRow>
                            <Notice fillWidth type="warning" visible={!currentEvent}>
                                Det eksisterer for øyeblikket ingen aktive arrangementer.<br/>
                            </Notice>
                        </InnerContainerRow>
                    </InnerContainer>

                    <InnerContainer rowgap>
                        <InnerContainerRow>
                            <InnerContainer flex="1" floattop>
                                <InnerContainer>
                                    <InnerContainerTitle>Filtrering og sortering</InnerContainerTitle>
                                    <InnerContainer>
                                        <CardContainer>
                                            <InputContainer column extramargin>
                                                <InputLabel small>Søk</InputLabel>
                                                <InputElement disabled={!tickets.length} type="text" placeholder="Billet ID, for- etternavn, brukernavn ..." onChange={(e) => setSearch(e.target.value.toLowerCase())}></InputElement>
                                            </InputContainer>
                                        </CardContainer>
                                    </InnerContainer>

                                    <InnerContainer>
                                        <CardContainer>
                                            <InputContainer column extramargin>
                                                <InputLabel small>Billettsortering</InputLabel>
                                                <InputSelect disabled={!tickets.length} onChange={(e) => setActiveSortingMethod(e.target.value)}>
                                                    <option value={SORTING_METHODS.TICKET_ID}>Billett ID</option>
                                                    <option value={SORTING_METHODS.TICKET_TYPE}>Billett type</option>
                                                    <option value={SORTING_METHODS.TICKET_OWNER}>Billett eier</option>
                                                    <option value={SORTING_METHODS.TICKED_CHECKED_IN}>Innsjekket</option>
                                                </InputSelect>
                                            </InputContainer>
                                        </CardContainer>
                                    </InnerContainer>
                                </InnerContainer>
                            </InnerContainer>

                            <InnerContainer flex="1" floattop>
                                <InnerContainer>
                                    <PanelButton onClick={exportCsv} icon={faDownload} disabled={!tickets.length}>Eksporter CSV</PanelButton>
                                </InnerContainer>
                            </InnerContainer>
                        </InnerContainerRow>
                    </InnerContainer>

                    <InnerContainer>
                        <Table>
                            <TableHead border>
                                <TableRow>
                                    <TableCell as="th" flex="1" mobileFlex="2">ID</TableCell>
                                    <TableCell as="th" flex="2" mobileHide>Billett type</TableCell>
                                    <TableCell as="th" flex="4" mobileFlex="7">Eies av bruker</TableCell>
                                    <TableCell as="th" flex="4" mobileHide>Kjøpt av bruker</TableCell>
                                    <TableCell as="th" flex="4" mobileHide>Seates av bruker</TableCell>
                                    <TableCell as="th" flex="2" mobileFlex="2">Sete</TableCell>
                                    <TableCell as="th" flex="3" mobileHide>Kjøpetid</TableCell>

                                    <TableCell as="th" center flex="0 24px" title="Statusikon: Ikon vises dersom billetten har blitt sjekket inn"><IconContainer>...</IconContainer></TableCell>
                                    <TableCell as="th" center flex="0 24px" title="Trykk for å åpne"><IconContainer>...</IconContainer></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {
                                    processedTicketList.map((ticket) => {
                                        return (
                                            <SelectableTableRow title="Trykk for å åpne" onClick={() => history.push(path(`/ticket/${ticket.ticket_id}`))} key={ticket.ticket_id}>
                                                <TableCell consolas flex="1" mobileFlex="2">#{ ticket.ticket_id }</TableCell>
                                                <TableCell flex="2" mobileHide>{ ticket.ticket_type.name }</TableCell>
                                                <TableCell flex="4" mobileFlex="7">{ User.getFullName(ticket.owner) }</TableCell>
                                                <TableCell flex="4" mobileHide>{ User.getFullName(ticket.buyer) }</TableCell>
                                                <TableCell flex="4" mobileHide>{ User.getFullName(ticket.seater) }</TableCell>
                                                <TableCell flex="2" mobileFlex="2">{ ticket.seat ? `R${ticket.seat.row.row_number} S${ticket.seat.number}` : "" }</TableCell>
                                                <TableCell flex="3" mobileHide>{ TimestampToDateTime(ticket.created, "DD_MM_YYYY_HH_MM") }</TableCell>
                                                <TableCell flex="0 24px" center>
                                                    {(!ticket.ticket_type.grants_admission) ? <IconContainer color="#616161"><FontAwesomeIcon icon={faMinus} title="Billetten er ikke en inngangsbillett og kan ikke sjekkes inn" /></IconContainer> : null}
                                                    {(ticket.checked_in) ? <IconContainer color="#388e3c"><FontAwesomeIcon icon={faCheck} title="Billetten er sjekket inn" /></IconContainer> : null}
                                                </TableCell>
                                                <TableCell flex="0 24px" center><IconContainer><FontAwesomeIcon icon={faArrowRight}/></IconContainer></TableCell>
                                            </SelectableTableRow>
                                        )
                                    })
                                }
                            </TableBody>
                        </Table>
                    </InnerContainer>
                </DashboardContent>
            </>
        )
    } else {
        return (
            <>
                <DashboardHeader border>
                    <DashboardTitle>
                        Billetter
                    </DashboardTitle>
                </DashboardHeader>
                <DashboardContent>
                    <InnerContainer rowgap>
                        <InnerContainerRow>
                            <Notice type="error" visible>
                                Du har ikke tilgang til å se billetter.
                            </Notice>
                        </InnerContainerRow>
                    </InnerContainer>
                </DashboardContent>
            </>
        )
    }
}
